<template>
  <a
    v-if="showMobileAppPrompt"
    class="mobile-app-link"
    :href="mobileAppUrl"
    target="_blank"
    rel="noopener noreferrer"
    :title="label"
    :aria-label="label"
  >
    <!-- Phone with a download arrow. Inline SVG: no icon-font dependency. -->
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
      <path d="M12 7.5v6" />
      <path d="M9.5 11.2 12 13.7l2.5-2.5" />
      <path d="M10 18h4" />
    </svg>
  </a>
</template>

<script setup>
import { computed } from "vue";
import { useMobileAppPrompt } from "@/composables/useMobileAppPrompt";
import { MOBILE_APP_NAME, STORE_NAMES } from "@/utils/MobileApp";

/**
 * Subtle "get the mobile app" icon for the top navigation bar.
 * Renders nothing unless the user is signed in, is not an admin, and is on a
 * device with a live store listing (Android today).
 */
const { showMobileAppPrompt, mobileAppUrl, platform } = useMobileAppPrompt();

const label = computed(
  () => `Get the ${MOBILE_APP_NAME} app on ${STORE_NAMES[platform] || "your device"}`
);
</script>

<style scoped>
.mobile-app-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(123, 128, 154, 0.35);
  color: #7b809a;
  background: transparent;
  text-decoration: none;
  transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
}

/* Keep the icon visually small while giving touch screens a comfortable 44px tap target. */
.mobile-app-link::after {
  content: "";
  position: absolute;
  inset: -6px;
}

.mobile-app-link svg {
  width: 18px;
  height: 18px;
}

.mobile-app-link:hover,
.mobile-app-link:focus-visible {
  color: #1a73e8;
  border-color: #1a73e8;
  background-color: rgba(26, 115, 232, 0.08);
}

.mobile-app-link:focus-visible {
  outline: 2px solid #1a73e8;
  outline-offset: 2px;
}
</style>
